import type { CambioRequisito } from '@/types/evaluacion';
import type { CalificacionType, CumplimientoNTC, EvaluacionAsesoria, Evidencia } from '@/types/types';
import { api } from './api';
import type { TernaDTO } from './sysadmin.service';

export interface RequisitoConAsesoria {
  id: string;
  ntc: number;
  calificacion: CalificacionType;
  calificacionAnterior?: CalificacionType | null;
  capitulo: string;
  numeral: string;
  posibleEvidencia: string;
  requisito: string;
  evaluacionRequisito: EvaluacionAsesoria;
  bloqueado?: boolean;
  bloqueadoPorAsesoriaId?: number;
  modificadaEnFuturas?: number[];
}

export interface RequisitoEvidencias {
  id: string;
  ntc: number;
  calificacion: CalificacionType;
  capitulo: string;
  numeral: string;
  posibleEvidencia: string;
  requisito: string;
  evaluacionRequisito: EvaluacionAsesoria;
}

export interface ComentarioAnterior {
  elementoTipo: string;
  elementoId: string | null;
  observacion: string;
}

export interface RequisitosConEvaluacionResponse {
  success: boolean;
  message: string;
  requisitos: RequisitoConAsesoria[];
  ntcs: CumplimientoNTC[];
  evidencias: Evidencia[];
  ternas: TernaDTO[];
  requisitosSinTerna: string[];
  evaluacionesTernas: EvaluacionTernaDTO[];
  evaluacionesTernasAnterior?: EvaluacionTernaAnteriorDTO[];
  comentariosAnteriores?: ComentarioAnterior[];
  cambioFieldLogs?: import('@/types/evaluacionAdmin').CambioFieldLog[];
  historialAnterior?: import('@/utils/revisionHistorial').RevisionEntry[];
  combinacion?: {
    combinacionId: number;
    nombre: string;
    ntcs: {
      ntcId: number;
      nombre: string;
      orden: number;
    }[];
  };
}

export interface EvaluacionTernaDTO {
  id: number;
  ternaId: number | null;
  grupo: string | null;
  observaciones: string | null;
  planMejora: string | null;
  fechaImplementacion: string | null;
  fechaEvaluacion: string;
  evidenciaIds: number[];
}

export interface EvaluacionTernaAnteriorDTO {
  ternaId: number | null;
  grupo: string | null;
  observaciones: string | null;
  planMejora: string | null;
  fechaImplementacion: string | null;
}

export interface CambioTernaEvaluacion {
  ternaId: number;
  observaciones?: string | null;
  planMejora?: string | null;
  fechaImplementacion?: string | null;
  evidenciaIds?: number[];
}

export interface BatchUpdateTernaResponse {
  success: boolean;
  message: string;
  data?: {
    actualizados: number[];
    totalActualizados: number;
    errores: { ternaId: number; error: string }[];
  };
}

export interface BatchUpdateRequest {
  nit: string;
  asesoriaId: number;
  cambios: Record<string, CambioRequisito>;
  eliminar?: string[];
}

export interface BatchUpdateResponse {
  success: boolean;
  message: string;
  data?: {
    actualizados: string[];
    totalActualizados: number;
    errores: { requisitoId: string; error: string }[];
  };
}

// Interfaz que coincide con la respuesta real del backend
export interface EstadoActualRequisito {
  asesoria: { observaciones: string; planMejora: string; fechaImplementacion: string; evidenciaIds: number[] } | null;
  requisitoId: string;
  ntcId: string;
  capitulo: string;
  numeral: string;
  descripcion: string;
  posiblesEvidencias?: { evidenciaId: string; nombre: string; url: string }[];
  ntc: {
    ntcId: number;
    nombre: string;
  };
  calificacion: 'C' | 'NC' | 'CP' | 'NA';
}

interface EstadoActualResponse {
  success: boolean;
  estadoActual?: EstadoActualRequisito[];
  message?: string;
}

// Requisito completo tal como llega en el nuevo array `requisitos[]` de la respuesta
interface RequisitoBaseDTO {
  requisitoId: string;
  ntcId: number;
  capitulo: string;
  numeral: string;
  descripcion: string;
  posibleEvidencia: string | null;
}

// Elemento ligero de `evaluacion[]` — solo calificación por requisito
interface RequisitoEvaluacionItem {
  requisitoId: string;
  calificacion: 'C' | 'NC' | 'CP' | 'NA';
  calificacionAnterior?: 'C' | 'NC' | 'CP' | 'NA' | null;
  evaluacionRequisito?: {
    id: number;
    calificacion: 'C' | 'NC' | 'CP' | 'NA';
    fechaEvaluacion: string;
    estadoRevision?: string;
    observacion?: string | null;
  };
  bloqueado?: boolean;
  bloqueadoPorAsesoriaId?: number;
  modificadaEnFuturas?: number[];
}

interface EvaluacionResponse {
  ntcs: CumplimientoNTC[];
  evidencias: Evidencia[];
  success: boolean;
  requisitos?: RequisitoBaseDTO[];
  evaluacion?: RequisitoEvaluacionItem[];
  ternas?: TernaDTO[];
  requisitosSinTerna?: string[];
  evaluacionesTernas?: EvaluacionTernaDTO[];
  evaluacionesTernasAnterior?: EvaluacionTernaAnteriorDTO[];
  comentariosAnteriores?: ComentarioAnterior[];
  historialAnterior?: import('@/utils/revisionHistorial').RevisionEntry[];
  message?: string;
  combinacion?: {
    combinacionId: number;
    nombre: string;
    ntcs: {
      ntcId: number;
      nombre: string;
      orden: number;
    }[];
  };
  cambioFieldLogs?: import('@/types/evaluacionAdmin').CambioFieldLog[];
}

export const requisitosService = {
  async getEvaluacionActiva(nit: string, requisitoId?: string): Promise<RequisitosConEvaluacionResponse> {
    // Evaluaciones completas usando el nuevo endpoint
    const queryParams = requisitoId ? `?requisitoId=${requisitoId}` : '';
    const response = await api.get<EvaluacionResponse>(`/empresas/${nit}/asesoria-activa/evaluaciones${queryParams}`);
    if (!response.success) {
      throw new Error(response.message || 'Error al cargar requisitos evaluados');
    }

    // Mapa O(1) de requisitos completos (nuevo array `requisitos[]` del backend)
    const requisitosBase = response.requisitos || [];
    const requisitosMap = new Map(requisitosBase.map((r) => [r.requisitoId, r]));

    // Mapear evaluacion[] + requisitosMap → RequisitoConAsesoria[]
    const evaluaciones = response.evaluacion || [];
    const requisitos: RequisitoConAsesoria[] = evaluaciones.map((item) => {
      const req = requisitosMap.get(item.requisitoId);
      const cal = item.evaluacionRequisito?.calificacion || item.calificacion;
      return {
        id: item.requisitoId,
        ntc: req?.ntcId ?? 0,
        calificacion: cal,
        calificacionAnterior: (item.calificacionAnterior as CalificacionType) ?? null,
        capitulo: req?.capitulo ?? '',
        numeral: req?.numeral ?? '',
        posibleEvidencia: req?.posibleEvidencia || '',
        requisito: req?.descripcion ?? '',
        evaluacionRequisito: {
          id: item.evaluacionRequisito?.id,
          calificacion: cal,
          estadoRevision: item.evaluacionRequisito?.estadoRevision,
          observacion: item.evaluacionRequisito?.observacion,
        },
        bloqueado: item.bloqueado,
        bloqueadoPorAsesoriaId: item.bloqueadoPorAsesoriaId,
        modificadaEnFuturas: item.modificadaEnFuturas,
      };
    });
    console.log(requisitos);

    // Mapear combinacion.ntcs a la forma CumplimientoNTC si existe
    let finalNtcs = response.ntcs;
    if (response.combinacion && response.combinacion.ntcs) {
      finalNtcs = response.combinacion.ntcs.map(
        (n) =>
          ({
            id: n.ntcId, // ID mock para cumplir la interfaz
            empresaId: nit,
            ntc: {
              ntcId: n.ntcId,
              nombre: n.nombre,
            },
            orden: n.orden,
            totalC: 0,
            totalNC: 0,
            totalCP: 0,
            totalNA: 0,
            totalRequisitos: 0,
            porcentajeCumplimiento: 0,
          }) as unknown as CumplimientoNTC
      );
    }

    return {
      success: true,
      message: '',
      requisitos,
      ntcs: finalNtcs,
      evidencias: response.evidencias,
      ternas: response.ternas || [],
      requisitosSinTerna: response.requisitosSinTerna || [],
      evaluacionesTernas: response.evaluacionesTernas || [],
      evaluacionesTernasAnterior: response.evaluacionesTernasAnterior,
      comentariosAnteriores: response.comentariosAnteriores || [],
      combinacion: response.combinacion,
      cambioFieldLogs: response.cambioFieldLogs || [],
      historialAnterior: response.historialAnterior || [],
    };
  },

  async getEstadoActual(nit: string, ntcId?: string): Promise<EstadoActualResponse> {
    // Estado actual cuando no hay asesoría ni filtro por NTC
    const response = await api.get<EstadoActualResponse>(`/empresas/${nit}/requisitos${ntcId ? `?ntcId=${ntcId}` : ''}`);
    if (!response.success) {
      throw new Error(response.message || 'Error al cargar estado actual de requisitos');
    }
    return {
      success: true,
      message: '',
      estadoActual: response.estadoActual,
    };
  },

  async getEvaluacion(nit: string, asesoriaId?: number, ntcId?: string, requisitoId?: string): Promise<RequisitosConEvaluacionResponse> {
    if (asesoriaId) {
      const queryParams = new URLSearchParams();
      queryParams.set('asesoriaId', String(asesoriaId));
      if (requisitoId) queryParams.set('requisitoId', requisitoId);
      const response = await api.get<EvaluacionResponse>(`/empresas/${nit}/asesoria-activa/evaluaciones?${queryParams.toString()}`);
      if (!response.success) throw new Error(response.message || 'Error al cargar requisitos evaluados');
      const requisitosBase = response.requisitos || [];
      const requisitosMap = new Map(requisitosBase.map((r) => [r.requisitoId, r]));
      const evaluaciones = response.evaluacion || [];
      const requisitos: RequisitoConAsesoria[] = evaluaciones.map((item) => {
        const req = requisitosMap.get(item.requisitoId);
        const cal = item.evaluacionRequisito?.calificacion || item.calificacion;
        return {
          id: item.requisitoId,
          ntc: req?.ntcId ?? 0,
          calificacion: cal,
          calificacionAnterior: (item.calificacionAnterior as CalificacionType) ?? null,
          capitulo: req?.capitulo ?? '',
          numeral: req?.numeral ?? '',
          posibleEvidencia: req?.posibleEvidencia || '',
          requisito: req?.descripcion ?? '',
          evaluacionRequisito: {
            id: item.evaluacionRequisito?.id,
            calificacion: cal,
            estadoRevision: item.evaluacionRequisito?.estadoRevision,
            observacion: item.evaluacionRequisito?.observacion,
          },
          bloqueado: item.bloqueado,
          bloqueadoPorAsesoriaId: item.bloqueadoPorAsesoriaId,
          modificadaEnFuturas: item.modificadaEnFuturas,
        };
      });
      return {
        success: true,
        message: '',
        requisitos,
        ntcs: response.ntcs || [],
        evidencias: response.evidencias || [],
        ternas: response.ternas || [],
        requisitosSinTerna: response.requisitosSinTerna || [],
        evaluacionesTernas: response.evaluacionesTernas || [],
        evaluacionesTernasAnterior: response.evaluacionesTernasAnterior,
        comentariosAnteriores: response.comentariosAnteriores || [],
        combinacion: response.combinacion ?? undefined,
        cambioFieldLogs: response.cambioFieldLogs || [],
        historialAnterior: response.historialAnterior || [],
      };
    }
    return this.getEvaluacionActiva(nit, requisitoId);
  },

  /**
   * Envía evaluaciones de requisitos de una asesoría usando el endpoint correcto (PATCH)
   * @param data BatchUpdateRequest
   * @returns BatchUpdateResponse
   */
  async batchUpdateTernas(nit: string, cambios: CambioTernaEvaluacion[], asesoriaId?: number): Promise<BatchUpdateTernaResponse> {
    const query = asesoriaId ? `?asesoriaId=${asesoriaId}` : '';
    return api.patch<BatchUpdateTernaResponse>(`/empresas/${nit}/asesoria-activa/evaluaciones/ternas${query}`, { cambios });
  },

  async batchUpdate(data: BatchUpdateRequest): Promise<BatchUpdateResponse> {
    const evaluaciones = Object.entries(data.cambios).map(([requisitoId, cambio]) => ({
      requisitoId,
      calificacion: cambio.calificacion,
    }));
    const query = data.asesoriaId ? `?asesoriaId=${data.asesoriaId}` : '';
    const response = await api.patch<BatchUpdateResponse>(`/empresas/${data.nit}/asesoria-activa/evaluaciones${query}`, {
      cambios: evaluaciones,
      ...(data.eliminar && data.eliminar.length > 0 ? { eliminar: data.eliminar } : {}),
    });
    return response;
  },
};
